import React, { useState, useEffect } from "react"
import { db } from "../../../firebaseClient"
import { doc, getDoc, collection, query, where, orderBy, limit, onSnapshot } from "firebase/firestore"
import { listUserEnrollments } from "../../../utils/enrollmentHelpers"
import { listUserCertificates } from "../../../utils/certHelpers"
import { getLearnerPoints } from "../../../utils/learnerPointsHelpers"
import { getCourse } from "../../../utils/courseHelpers"
import Icon from "../../../components/AppIcon"
import ProfileCard from "../../../components/profile-card/ProfileCard"

const formatEventType = (type) =>
  (type || "").split("_").map((w) => w.charAt(0) + w.slice(1).toLowerCase()).join(" ")

const formatTimestamp = (ts) => {
  const ms = ts?.toMillis ? ts.toMillis() : null
  return ms ? new Date(ms).toLocaleString() : "—"
}

const LearnerDetail = ({ uid, onBack }) => {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [enrollments, setEnrollments] = useState([])
  const [certificates, setCertificates] = useState([])
  const [totalPoints, setTotalPoints] = useState(0)
  const [loading, setLoading] = useState(true)
  const [events, setEvents] = useState([])

  useEffect(() => {
    const load = async () => {
      const [userSnap, profileSnap, { enrollments: enr }, { certificates: certs }, { totalPoints: pts }] =
        await Promise.all([
          getDoc(doc(db, "users", uid)),
          getDoc(doc(db, "learnerProfiles", uid)),
          listUserEnrollments(uid),
          listUserCertificates(uid),
          getLearnerPoints(uid),
        ])
      setUser(userSnap.exists() ? userSnap.data() : null)
      setProfile(profileSnap.exists() ? profileSnap.data() : null)
      const withTitles = await Promise.all(
        (enr || []).map(async (e) => {
          if (!e?.orgId || !e?.courseId) return e
          const { course } = await getCourse(e.orgId, e.courseId)
          return { ...e, courseTitle: course?.title || e.courseId }
        })
      )

      setEnrollments(withTitles)
      setCertificates(certs || [])
      setTotalPoints(pts || 0)
      setLoading(false)
    }
    load()
  }, [uid])

  useEffect(() => {
    const q = query(
      collection(db, "events"),
      where("actorUid", "==", uid),
      orderBy("createdAt", "desc"),
      limit(15)
    )
    const unsubscribe = onSnapshot(q, (snap) => {
      setEvents(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    })
    return () => unsubscribe()
  }, [uid])

  if (loading) {
    return <div className="p-6 text-center text-muted-foreground text-sm">Loading learner...</div>
  }

  if (!user) {
    return (
      <div className="p-6 text-center text-muted-foreground text-sm">
        User not found.
        <button onClick={onBack} className="block mx-auto mt-3 text-sm text-primary hover:underline">
          ← Back to People
        </button>
      </div>
    )
  }

  const completedCount = enrollments?.filter((e) => (e?.progressPercent || 0) >= 100)?.length || 0
  const distinctOrgIds = [...new Set(enrollments?.map((e) => e?.orgId).filter(Boolean))]

  return (
    <div className="space-y-6">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <Icon name="ArrowLeft" size={14} />
        Back to People
      </button>

      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-center gap-3">
          {user?.avatarUrl ? (
            <img src={user.avatarUrl} alt={user?.name} className="size-12 rounded-full object-cover" />
          ) : (
            <div className="size-12 rounded-full bg-muted" />
          )}
          <div>
            <h2 className="text-lg font-bold text-foreground">{user?.name || "Unnamed"}</h2>
            <p className="text-sm text-muted-foreground">{user?.email} · {user?.nesolearnId || uid}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-5 pt-5 border-t border-border">
          <div>
            <p className="text-xs text-muted-foreground">Enrollments</p>
            <p className="text-xl font-bold text-foreground">{enrollments?.length || 0}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Completed</p>
            <p className="text-xl font-bold text-foreground">{completedCount}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Credentials</p>
            <p className="text-xl font-bold text-foreground">{certificates?.length || 0}</p>
          </div>
           <div>
            <p className="text-xs text-muted-foreground">Points</p>
            <p className="text-xl font-bold text-foreground">{totalPoints?.toLocaleString?.() || 0}</p>
          </div>
        </div>
      </div>

      {profile?.nesolearnId && (
        <div>
          <h3 className="font-semibold text-foreground mb-2 px-1">Public Profile Preview</h3>
          <ProfileCard profile={profile} />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-border">
            <h3 className="font-semibold text-foreground">Enrollments</h3>
          </div>
          {enrollments?.length === 0 ? (
            <div className="p-4 text-center text-muted-foreground text-sm">No enrollments.</div>
          ) : (
            <div className="divide-y divide-border">
              {enrollments?.map((e) => (
                                <div key={e?.enrollmentId} className="px-4 py-2.5 flex items-center justify-between gap-3">
                  <span className="text-sm text-foreground truncate">{e?.courseTitle}</span>
                  <span className="text-xs text-muted-foreground shrink-0">{e?.progressPercent || 0}%</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-border">
            <h3 className="font-semibold text-foreground">Credentials</h3>
          </div>
          {certificates?.length === 0 ? (
            <div className="p-4 text-center text-muted-foreground text-sm">No credentials issued.</div>
          ) : (
            <div className="divide-y divide-border">
              {certificates?.map((c) => (
                <div key={c?.certId} className="px-4 py-2.5 flex items-center justify-between gap-3">
                  <span className="text-sm text-foreground truncate">{c?.courseTitle}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${
                    c?.status === "revoked" ? "bg-destructive/10 text-destructive" : "bg-success/10 text-success"
                  }`}>
                    {c?.status === "revoked" ? "Revoked" : "Valid"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {distinctOrgIds?.length > 0 && (
        <div className="bg-card border border-border rounded-xl p-4">
          <h3 className="font-semibold text-foreground mb-2">Organization Memberships</h3>
          <div className="flex flex-wrap gap-2">
            {distinctOrgIds?.map((orgId) => (
              <span key={orgId} className="text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground font-mono">
                {orgId}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-border">
          <h3 className="font-semibold text-foreground">Recent Activity</h3>
        </div>
        {events?.length === 0 ? (
          <div className="p-6 text-center text-muted-foreground text-sm">No recorded activity yet.</div>
        ) : (
          <div className="divide-y divide-border">
            {events?.map((event) => (
              <div key={event?.id} className="px-4 py-3 flex items-center justify-between gap-3">
                <span className="text-sm text-foreground">{formatEventType(event?.type)}</span>
                <span className="text-xs text-muted-foreground shrink-0">{formatTimestamp(event?.createdAt)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default LearnerDetail